/**
 * A study's scores read across its whole cohort, for the study dashboard.
 *
 * **Assembled by the API, never produced by Python**, exactly like
 * `AnalysisResultSchema`: the scoring service is stateless and scores one
 * scope at a time, and it knows nothing about studies, enrolments or cohorts.
 * So this file is deliberately NOT in `analysis.ts` and is NOT emitted to
 * `services/analysis/contracts/` — `scripts/emit-json-schema.ts` names what it
 * emits, and nothing here is on that list. Reshaping this envelope is a
 * TypeScript-only change.
 *
 * **It is an AGGREGATE, not a bigger envelope.** A per-patient read hands the
 * plugin's own blocks straight through, which is what keeps the dashboard from
 * ever deciding what a number means. Here the same blocks are folded across
 * patients, and the only two operations used are ones that cannot invent
 * clinical meaning:
 *
 * - **counting** patients per `level`, which the plugin already resolved for
 *   direction (a T-score of 68 arrives as `severe` on anxiety and `excellent`
 *   on physical function, and this file never learns the difference);
 * - **averaging** `value` within ONE block key, where every patient's number
 *   is on the same instrument, the same unit and the same scale.
 *
 * There is deliberately no composite "risk score" across domains. Weighing
 * anxiety against physical function into one number is a clinical claim, it
 * belongs to a plugin that can be validated, and inventing one here would put
 * the PROMIS trap back on a page where nothing could catch it. `worstLevel`
 * below is the honest substitute: a MAXIMUM over an ordered enum the plugin
 * wrote, which selects a finding rather than computing one.
 */
import { z } from "zod";

import {
  ANALYSIS_CONTRACT_VERSION,
  AnalysisUnitSchema,
  SeverityLevelSchema,
  DeltaDirectionSchema,
  type SeverityLevel,
} from "./analysis";
import { TranslationMapSchema } from "./utils";

/**
 * How severe beats how severe, for `worstLevel`.
 *
 * `unknown` is off the scale on purpose rather than ranked at the bottom: it
 * means "no reference population", which is not a mild finding, and sorting it
 * beside real levels would let a patient nobody can interpret read as the
 * healthiest in the cohort. A patient whose every block is `unknown` reports
 * `unknown`, which is a statement.
 */
const SEVERITY_ORDER: Record<SeverityLevel, number> = {
  excellent: 0,
  normal: 1,
  mild: 2,
  moderate: 3,
  severe: 4,
  unknown: -1,
};

/**
 * The most severe of a patient's findings, or `unknown` when none of them sits
 * on the scale at all. Null for a patient with no findings.
 *
 * A selection, never a computation: the answer is always a level some plugin
 * emitted about some domain, which is what makes it explainable when a
 * clinician asks why a row is red.
 */
export function worstSeverity(
  levels: readonly SeverityLevel[],
): SeverityLevel | null {
  if (levels.length === 0) return null;
  const ranked = levels.filter((level) => SEVERITY_ORDER[level] >= 0);
  if (ranked.length === 0) return "unknown";
  return ranked.reduce((worst, level) =>
    SEVERITY_ORDER[level] > SEVERITY_ORDER[worst] ? level : worst,
  );
}

/** `mild` and up: what a clinician would look at twice. Mirrors the
 *  dashboard's `isNotable`, which decides whether a chip is drawn at all. */
export function isNotableLevel(level: SeverityLevel): boolean {
  return level === "mild" || level === "moderate" || level === "severe";
}

/** Every level, so a distribution has a bar for zero rather than a hole. */
const LevelCountsSchema = z.object({
  excellent: z.number().int(),
  normal: z.number().int(),
  mild: z.number().int(),
  moderate: z.number().int(),
  severe: z.number().int(),
  unknown: z.number().int(),
});
export type LevelCounts = z.infer<typeof LevelCountsSchema>;

/**
 * One of the questionnaire's findings, folded across the cohort.
 *
 * Named "domain" because that is what a plugin's blocks are in practice, and
 * it covers both kinds this read aggregates: a `score` block contributes
 * numbers and a level, a `flag` block contributes a level alone. `trend` blocks
 * are deliberately dropped — see `StudyAnalysisSchema` below.
 */
export const CohortDomainSchema = z.object({
  /** The block's key, unique within the questionnaire. */
  key: z.string(),
  /** A key into `labels`, never a sentence: clinical vocabulary is the
   *  plugin's, and the bundle travels in the same envelope. */
  titleKey: z.string(),
  /** Direction affordances only. Colour comes from `level`, as everywhere. */
  higherIsBetter: z.boolean().nullable(),
  /** Null for a `flag`, which carries a level and no number. */
  unit: AnalysisUnitSchema.nullable(),
  range: z.object({ min: z.number(), max: z.number() }).nullable(),
  /**
   * The cohort mean of each patient's LATEST value for this block, or null
   * when nothing numeric could be scored. `n` is how many patients that mean
   * is over, which is never assumed to be the cohort size: a domain can be
   * `unavailable` for one patient and fine for the next.
   */
  mean: z.number().nullable(),
  n: z.number().int(),
  /** How many patients sit at each level. Sums to the number of patients whose
   *  block was computable. */
  levels: LevelCountsSchema,
  /** Patients whose block came back `unavailable`, for any reason. */
  unavailable: z.number().int(),
});
export type CohortDomain = z.infer<typeof CohortDomainSchema>;

/** One patient's latest reading of one block, for the roster. */
export const CohortPatientScoreSchema = z.object({
  value: z.number().nullable(),
  level: SeverityLevelSchema,
  delta: z
    .object({ value: z.number(), direction: DeltaDirectionSchema })
    .nullable(),
});

/**
 * A row of the roster: one enrolled patient, as this questionnaire sees them.
 *
 * Names are here because the read is already gated on study membership
 * (`accessService.assertStudy`, which answers 404 to everyone else), and a
 * cohort table a clinician cannot read names in is a table of ids.
 */
export const CohortPatientSchema = z.object({
  patientStudyId: z.string(),
  patientId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  /** When they last answered THIS questionnaire, null if never. */
  lastSubmittedAt: z.iso.datetime().nullable(),
  submissions: z.number().int(),
  /** The most severe finding, by `worstSeverity` above. Null when nothing was
   *  computable, which is what an enrolled patient who has not answered looks
   *  like. */
  worstLevel: SeverityLevelSchema.nullable(),
  /** How many of their findings are `mild` or worse. The mockup's "alerts",
   *  named after what it actually counts. */
  notable: z.number().int(),
  /** Keyed by block key. A block missing from the map was `unavailable` for
   *  this patient — the same "cannot be computed is a state" rule, expressed
   *  as absence here because the domain row already carries the count. */
  scores: z.record(z.string(), CohortPatientScoreSchema),
});
export type CohortPatient = z.infer<typeof CohortPatientSchema>;

/**
 * The study dashboard's payload: ONE questionnaire, every enrolled patient.
 *
 * **`surveyId` is required, and that is the cost decision.** A per-patient read
 * already narrows to one instrument because scoring the others can each mean a
 * model call; multiply that by a cohort and a page load would fan out to every
 * patient times every scored questionnaire in the study. One instrument at a
 * time, selected on the page, is the same discipline the per-patient page
 * settled on for the same reason.
 *
 * **`trend` blocks are not aggregated.** They carry each patient's own
 * submission instants, and patients enrol on different days, so averaging them
 * on calendar time compares a patient's week 1 against another's week 20. The
 * honest cohort axis is time SINCE ENROLMENT, which needs binning decisions
 * (width, how to treat a patient who left) that belong in their own pass rather
 * than being guessed at here. Until then the page says nothing about trends
 * rather than drawing one that is wrong.
 */
export const StudyAnalysisSchema = z.object({
  contractVersion: z.literal(ANALYSIS_CONTRACT_VERSION),
  surveyId: z.string(),
  /** The catalog title, the one thing Python cannot know. */
  title: TranslationMapSchema,
  /**
   * `labelKey` -> text for every key the domains reference, exactly as a
   * per-patient envelope carries it. One questionnaire means one plugin means
   * one bundle, so there is nothing to merge and nothing to collide.
   *
   * Empty when no patient has been scored yet: the bundle comes back with a
   * result, and there is no result to take it from.
   */
  labels: z.record(z.string(), TranslationMapSchema),
  cohort: z.object({
    /** Everyone enrolled, including patients who have answered nothing. */
    enrolled: z.number().int(),
    /** How many of them this questionnaire has an answer from. */
    answered: z.number().int(),
    /** Patients holding at least one `mild`-or-worse finding. */
    withNotable: z.number().int(),
    /** Patients holding at least one `severe` finding. */
    withSevere: z.number().int(),
  }),
  domains: z.array(CohortDomainSchema),
  patients: z.array(CohortPatientSchema),
});
export type StudyAnalysis = z.infer<typeof StudyAnalysisSchema>;

/**
 * `surveyId` is a required query parameter rather than a path segment because
 * it selects a questionnaire the study attached, not a sub-resource of the
 * study — the same shape the two per-patient reads use, where it is optional
 * only because those predate the narrowing.
 */
export const getStudyAnalysisSchemas = {
  params: z.object({ studyId: z.string() }),
  querystring: z.object({ surveyId: z.string() }),
  response: { 200: StudyAnalysisSchema },
};
